/**
 * DateSelectionField Component
 * Field for date selection based on mode
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    FormControl,
    FormLabel,
    FormHelperText,
    Select,
    MenuItem,
    FormControl as MuiFormControl,
    Switch,
    FormControlLabel,
    Typography,
} from '@mui/material';
import { Calendar } from '@dynamon-as/ui';
import { ButtonGroup, Button } from '@dynamon-as/ui';
import { ShiftPreference, RequestType } from '@features/requests/types';
import type { DateSelectionMode } from '@features/requests/types/dateSelectionTypes';
import type {
    CalendarValue,
    SelectionType,
    SingleDateValue,
    CalendarProps,
} from '@dynamon-as/ui';
import styles from './DateSelectionField.module.css';

/**
 * Mode labels for accessibility and UI
 */
const MODE_LABELS: Record<DateSelectionMode, string> = {
    single: 'Dato',
    period: 'Periode',
    loose_days: 'Enkeltdager',
    shift: 'Skift',
};

/**
 * Shift configuration for selection
 */
const SHIFTS_CONFIG = [
    { id: ShiftPreference.MORNING, label: 'Morgen' },
    { id: ShiftPreference.AFTERNOON, label: 'Ettermiddag' },
    { id: ShiftPreference.NIGHT, label: 'Kveld' },
    { id: ShiftPreference.WEEKEND, label: 'Helg' },
    { id: ShiftPreference.OTHER, label: 'Annet' },
] as const;

/**
 * Props for DateSelectionField component
 */
export interface DateSelectionFieldProps {
    /** Date selection mode */
    mode: DateSelectionMode;
    /** Type of selection: single, range, or multiple */
    selectionType: SelectionType;
    /** Current value of the date selection */
    value: SingleDateValue | null;
    /** Callback when date selection changes */
    onChange: (newValue: CalendarValue) => void;
    /** Currently selected shift preference */
    shiftPreference?: ShiftPreference;
    /** Callback when shift is selected */
    onShiftSelect: (shiftId: ShiftPreference) => void;
    /** Error messages for date and shift selection */
    errors: {
        dateSelection?: string;
        shiftSelection?: string;
    };
    /** Minimum selectable date */
    minDate?: Date;
    /** Maximum selectable date */
    maxDate?: Date;
    /** Dates that should be disabled in the calendar */
    disabledDates?: Date[];
    /** Dates that the employee has excluded from the pattern */
    excludedDates?: Date[];
    /** Callback when excluded dates change */
    onExcludedDatesChange?: (dates: Date[]) => void;
    /** Request type to determine UI behavior */
    requestType?: RequestType;
}

/**
 * DateSelectionField component
 */
export function DateSelectionField({
    mode,
    selectionType,
    value,
    onChange,
    shiftPreference,
    onShiftSelect,
    errors,
    minDate,
    maxDate,
    disabledDates,
    excludedDates = [],
    onExcludedDatesChange,
    requestType,
}: DateSelectionFieldProps): React.ReactElement {
    const hasError = !!errors.dateSelection || !!errors.shiftSelection;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const errorMessage = errors.dateSelection || errors.shiftSelection;
    const isShiftPreference = requestType === RequestType.SHIFT_PREFERENCE;

    // State for excluding dates toggle
    const [excludeModeEnabled, setExcludeModeEnabled] = useState(false);

    // Handle toggle of excluded dates
    const handleExcludedDateToggle = useCallback(
        (date: Date): void => {
            if (!onExcludedDatesChange) return;

            const normalizedDate = new Date(date);
            normalizedDate.setHours(0, 0, 0, 0);

            // Check if date is already in excludedDates
            const isExcluded = excludedDates?.some((d): boolean => {
                const existingDate = new Date(d);
                existingDate.setHours(0, 0, 0, 0);
                return existingDate.getTime() === normalizedDate.getTime();
            });

            let newExcludedDates: Date[];
            if (isExcluded) {
                // Remove from excludedDates
                newExcludedDates =
                    excludedDates?.filter((d): boolean => {
                        const existingDate = new Date(d);
                        existingDate.setHours(0, 0, 0, 0);
                        return (
                            existingDate.getTime() !== normalizedDate.getTime()
                        );
                    }) ?? [];
            } else {
                // Add to excludedDates
                newExcludedDates = [...(excludedDates ?? []), date];
            }

            onExcludedDatesChange(newExcludedDates);
        },
        [excludedDates, onExcludedDatesChange],
    );

    // Detect mobile screen size
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = (): void => {
            setIsMobile(window.innerWidth <= 768);
        };

        // Initial check
        checkMobile();

        // Add event listener for window resize
        window.addEventListener('resize', checkMobile);

        // Cleanup
        return (): void => window.removeEventListener('resize', checkMobile);
    }, []);

    // Use double calendar for desktop, single for mobile
    const calendarMode: 'single' | 'double' = isMobile ? 'single' : 'double';

    // Check if value exists for shift button enable/disable
    // value can be Date, null, or undefined
    const hasValue = value !== null && value !== undefined;

    return (
        <div
            className={styles.field}
            data-testid="date-selection-field"
        >
            <FormControl
                fullWidth
                error={hasError}
            >
                <FormLabel
                    id="date-selection-label"
                    className={styles.fieldLabel}
                >
                    {MODE_LABELS[mode]}
                </FormLabel>

                {/* Toggle for excluding dates mode */}
                {onExcludedDatesChange && (
                    <div className={styles.excludeToggle}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={excludeModeEnabled}
                                    onChange={(e): void =>
                                        setExcludeModeEnabled(e.target.checked)
                                    }
                                    size="small"
                                    color="primary"
                                    data-testid="exclude-dates-toggle"
                                />
                            }
                            label={
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Ekskluder dager
                                </Typography>
                            }
                            labelPlacement="end"
                        />
                    </div>
                )}

                {/* Calendar - double for desktop, single for mobile */}
                <Calendar
                    {...({
                        mode: calendarMode,
                        selectionType: selectionType,
                        value: value as CalendarValue,
                        onChange: onChange,
                        locale: 'nb',
                        minDate: minDate,
                        maxDate: maxDate,
                        disabledDates: disabledDates,
                        excludedDates: excludedDates,
                        onExcludedDateToggle: handleExcludedDateToggle,
                        excludeModeEnabled: excludeModeEnabled,
                        'aria-label': `Velg ${MODE_LABELS[mode].toLowerCase()} for søknaden`,
                        'data-testid': 'date-calendar',
                    } as CalendarProps)}
                />

                {/* Shift selection - ButtonGroup for desktop, Select for mobile */}
                {isShiftPreference && (
                    <div className={styles.shiftButtonsContainer}>
                        <div className={styles.shiftButtonsPaper}>
                            <FormLabel className={styles.shiftButtonsLabel}>
                                Velg skift
                            </FormLabel>
                            {isMobile ? (
                                <MuiFormControl
                                    fullWidth
                                    size="small"
                                >
                                    <Select
                                        value={shiftPreference ?? ''}
                                        onChange={(e): void =>
                                            onShiftSelect(
                                                e.target
                                                    .value as ShiftPreference,
                                            )
                                        }
                                        disabled={!hasValue}
                                        displayEmpty
                                        data-testid="shift-select"
                                    >
                                        <MenuItem
                                            value=""
                                            disabled
                                        >
                                            Velg skift
                                        </MenuItem>
                                        {SHIFTS_CONFIG.map(
                                            (shift): React.ReactElement => (
                                                <MenuItem
                                                    key={shift.id}
                                                    value={shift.id}
                                                    data-testid={`shift-${shift.id.toLowerCase()}-option`}
                                                >
                                                    {shift.label}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </MuiFormControl>
                            ) : (
                                <ButtonGroup
                                    size="small"
                                    variant="outlined"
                                    data-testid="shift-buttons"
                                >
                                    {SHIFTS_CONFIG.map(
                                        (shift): React.ReactElement => (
                                            <Button
                                                key={shift.id}
                                                text={shift.label}
                                                variant={
                                                    shiftPreference === shift.id
                                                        ? 'normal'
                                                        : 'outlined'
                                                }
                                                onClick={(): void =>
                                                    onShiftSelect(shift.id)
                                                }
                                                disabled={!hasValue}
                                                className={styles.shiftButton}
                                                type="button"
                                                aria-label={`Velg ${shift.label.toLowerCase()} skift`}
                                                aria-disabled={!hasValue}
                                                data-testid={`shift-${shift.id.toLowerCase()}-button`}
                                            />
                                        ),
                                    )}
                                </ButtonGroup>
                            )}
                        </div>
                    </div>
                )}

                {hasError && (
                    <FormHelperText
                        error
                        data-testid="date-error-message"
                    >
                        {errorMessage}
                    </FormHelperText>
                )}

                {/* Summary of excluded dates */}
                {onExcludedDatesChange &&
                    excludedDates &&
                    excludedDates.length > 0 && (
                        <div
                            className={styles.excludedSummary}
                            data-testid="excluded-dates-summary"
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Ekskluderte dager: {excludedDates.length}
                                {excludedDates.length > 0 && (
                                    <>
                                        {': '}
                                        {excludedDates
                                            .slice(0, 3)
                                            .map(
                                                (
                                                    date,
                                                    index,
                                                ): React.ReactElement => (
                                                    <span
                                                        key={date.toISOString()}
                                                    >
                                                        {index > 0 && ', '}
                                                        {date.toLocaleDateString(
                                                            'nb-NO',
                                                            {
                                                                day: 'numeric',
                                                                month: 'short',
                                                            },
                                                        )}
                                                    </span>
                                                ),
                                            )}
                                        {excludedDates.length > 3 && (
                                            <span>
                                                , +{excludedDates.length - 3}{' '}
                                                til
                                            </span>
                                        )}
                                    </>
                                )}
                            </Typography>
                        </div>
                    )}
            </FormControl>
        </div>
    );
}

export default DateSelectionField;
